import React, { useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css"; // Quill Editor styles
import { createDiary } from "./.request";
import { useAuth } from "../context/AuthContext";
import pdf from '../../assets/icons/pdf.svg';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';

const CreateNoorDiary = () => {
  const [year, setYear] = useState("");
  const [saal, setSaal] = useState("");
  const [coverImage, setCoverImage] = useState(null);
  const [pdfBase64, setPdfBase64] = useState('');
  const [coverPreview, setCoverPreview] = useState(null);
  const [muqadma, setMuqadma] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { authToken } = useAuth();

  // Convert image to Base64 and preview
  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type and size
      if (!file.type.startsWith("image/")) {
        alert("Please upload a valid image file.");
        return;
      }
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        alert("Image size must be less than 5MB.");
        return;
      }

      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onloadend = () => {
        setCoverImage(reader.result);
        setCoverPreview(reader.result);
      };
    }
  };

  // Convert PDF to Base64
  const handlePdfUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type and size
      if (file.type !== "application/pdf") {
        alert("Please upload a valid PDF file.");
        return;
      }
      

      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onloadend = () => {
        setPdfBase64(reader.result);
      };
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    // Validate required fields
    if (!year || !saal || !coverImage || !muqadma) {
      alert("Please fill in all required fields (Year, Year in Urdu, Cover Image, Muqadma, and PDF).");
      return;
    }

    const formData = {
      year,
      saal,
      coverImage,
      muqadma,
      pdf: pdfBase64, // Include PDF in formData
    };

    try {
      setIsSubmitting(true);
      const response = await createDiary(authToken, formData);
      if (response.Result) {
        alert("Noor Diary created successfully!");
        // Reset all fields
        setYear("");
        setSaal("");
        setCoverImage(null);
        setCoverPreview(null);
        setMuqadma("");
        setPdfBase64("");
      } else {
        alert(response.data.Msg || "Failed to create diary.");
      }
    } catch (error) {
      console.error("Error creating Noor Diary:", error);
      alert(error.response?.data?.message || "An error occurred while submitting the form.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="mx-auto p-6 bg-white shadow-lg rounded-lg">
      <h2 className="text-2xl font-bold mb-4">Create Noor Diary</h2>
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Year Input */}
        <div>
          <label className="block font-semibold" htmlFor="year">
            Year:
          </label>
          <input
            id="year"
            type="text"
            className="w-full border p-2 rounded-md"
            value={year}
            onChange={(e) => setYear(e.target.value)}
            placeholder="Enter year"
            aria-label="Year"
            required
          />
        </div>
        <div>
          <label className="block font-semibold" htmlFor="saal">
            Year (Urdu):
          </label>
          <input
            id="saal"
            type="text"
            className="w-full border p-2 rounded-md"
            value={saal}
            onChange={(e) => setSaal(e.target.value)}
            placeholder="Enter year in Urdu"
            aria-label="Year in Urdu"
            required
          />
        </div>

        {/* Cover Image Upload */}
        <div>
          <label className="block font-semibold" htmlFor="coverImage">
            Cover Image:
          </label>
          <input
            id="coverImage"
            type="file"
            className="w-full border p-2 rounded-md"
            accept="image/*"
            onChange={handleImageUpload}
            aria-label="Upload cover image"
            required
          />

          {/* Image Preview */}
          {coverPreview && (
            <div className="mt-3">
              <img
                src={coverPreview}
                alt="Cover Preview"
                className="w-40 h-32 object-cover rounded-md shadow"
              />
            </div>
          )}
        </div>

        {/* Muqadma (Quill Editor) */}
        <div>
          <label className="block font-semibold">Muqadma:</label>
          <ReactQuill
            value={muqadma}
            onChange={setMuqadma}
            className="bg-white"
            aria-label="Muqadma text editor"
          />
        </div>

        {/* PDF Upload */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="pdf">
            Upload PDF
          </label>
          <input
            id="pdf"
            type="file"
            accept=".pdf"
            onChange={handlePdfUpload}
            className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            aria-label="Upload PDF file"
            required
          />
        </div>

        {/* PDF Download Link */}
        {pdfBase64 && (
          <div className="mt-4 flex items-center gap-2">
            <img src={pdf} alt="PDF icon" className="size-7" />
            <a
              href={pdfBase64}
              download={`${year || "noor-diary"}.pdf`}
              className="text-blue-500 underline"
            >
              Download PDF
            </a>
          </div>
        )}

        {/* Submit Button */}
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={isSubmitting}
        >
          {isSubmitting ? <CircularProgress size={20} color="inherit" /> : "Submit"}
        </Button>
      </form>
    </div>
  );
};

export default CreateNoorDiary;